import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Upload, 
  Download, 
  Calculator, 
  Percent, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Save, 
  FileSpreadsheet, 
  ChevronRight,
  TrendingUp,
  DollarSign,
  User,
  Info,
  Search
} from 'lucide-react';
import { getPayrollPeriods } from '../services/payrollPeriodService';
import { getStaff, Staff } from '../services/staffService';
import { 
  importGrossReceiptsCSV, 
  calculateShortfall, 
  saveShortfallAllocations, 
  getShortfallAllocationsForPeriod,
  ShortfallCalculationResult,
  getAllShortfallAllocations
} from '../services/grossReceiptsService';
import { PayrollPeriod, ShortfallAllocation } from '../types';
import { formatCurrency, formatHours, formatPercentage } from '../lib/formatters';

interface Props {
  tenantId: string;
}

export const GrossReceiptsDashboard: React.FC<Props> = ({ tenantId }) => {
  // Period Selection
  const [payrollPeriods, setPayrollPeriods] = useState<PayrollPeriod[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [rate, setRate] = useState<number>(0.08); // Standard IRS 8% rate

  // Staff and Methods
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [allocationMethod, setAllocationMethod] = useState<'hours_worked' | 'gross_receipts' | 'good_faith'>('hours_worked');
  const [employeeSalesInput, setEmployeeSalesInput] = useState<Record<string, number>>({});

  // Calculation State
  const [calcResult, setCalcResult] = useState<ShortfallCalculationResult | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // File Upload State
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [importedFilename, setImportedFilename] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tab & History State
  const [activeTab, setActiveTab] = useState<'calculator' | 'history'>('calculator');
  const [historyList, setHistoryList] = useState<ShortfallAllocation[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState<boolean>(false);

  // Load initial data
  useEffect(() => {
    if (!tenantId) return;
    const loadInitialData = async () => {
      try {
        const [periods, staff] = await Promise.all([
          getPayrollPeriods(tenantId),
          getStaff(tenantId)
        ]);
        setPayrollPeriods(periods);
        setStaffList(staff);
        
        // Auto-select latest period if available
        if (periods.length > 0) {
          const latest = periods[0];
          setSelectedPeriodId(latest.id || '');
          setStartDate(latest.startDate);
          setEndDate(latest.endDate);
        } else {
          // Default to custom date range with a standard 14-day window if no periods exist
          setSelectedPeriodId('custom');
          const today = new Date();
          const fourteenDaysAgo = new Date();
          fourteenDaysAgo.setDate(today.getDate() - 14);
          
          setStartDate(fourteenDaysAgo.toISOString().split('T')[0]);
          setEndDate(today.toISOString().split('T')[0]);
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
      }
    };
    loadInitialData();
  }, [tenantId]);

  const loadHistory = async () => {
    if (!tenantId) return;
    setIsHistoryLoading(true);
    try {
      const data = await getAllShortfallAllocations(tenantId);
      setHistoryList(data);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history' && tenantId) {
      loadHistory();
    }
  }, [tenantId, activeTab]);

  // Handle period change
  const getDateRangeForPreset = (preset: string): { start: string, end: string } => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth(); // 0-indexed

    switch (preset) {
      case 'monthly': {
        const start = new Date(y, m, 1);
        const end = new Date(y, m + 1, 0); // last day of month
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        };
      }
      case 'quarterly': {
        const q = Math.floor(m / 3); // 0, 1, 2, 3
        const start = new Date(y, q * 3, 1);
        const end = new Date(y, (q + 1) * 3, 0);
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        };
      }
      case 'annual': {
        const start = new Date(y, 0, 1);
        const end = new Date(y, 11, 31);
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        };
      }
      default: { // 'custom' or default 14-day window
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(today.getDate() - 14);
        return {
          start: fourteenDaysAgo.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      }
    }
  };

  const handlePeriodChange = (periodId: string) => {
    setSelectedPeriodId(periodId);
    if (['custom', 'monthly', 'quarterly', 'annual'].includes(periodId)) {
      const { start, end } = getDateRangeForPreset(periodId);
      setStartDate(start);
      setEndDate(end);
      return;
    }
    const period = payrollPeriods.find(p => p.id === periodId);
    if (period) {
      setStartDate(period.startDate);
      setEndDate(period.endDate);
    }
  };

  // Run calculation dynamically
  const runCalculation = async () => {
    if (!tenantId || !startDate || !endDate) return;
    setIsLoading(true);
    try {
      const res = await calculateShortfall(
        tenantId,
        startDate,
        endDate,
        allocationMethod,
        rate,
        employeeSalesInput
      );
      setCalcResult(res);
      
      // Initialize employee sales inputs if empty and method is gross_receipts
      if (allocationMethod === 'gross_receipts' && Object.keys(employeeSalesInput).length === 0) {
        const initialSales: Record<string, number> = {};
        staffList.forEach(s => {
          initialSales[s.id!] = 0;
        });
        setEmployeeSalesInput(initialSales);
      }
    } catch (err: any) {
      console.error('Calculation error:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to run shortfall calculation.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Run calculation when period, dates, rate, method, or sales inputs change
  useEffect(() => {
    runCalculation();
  }, [tenantId, startDate, endDate, allocationMethod, rate, employeeSalesInput]);

  // Handle individual employee sales inputs (Gross Receipts method)
  const handleEmployeeSalesChange = (staffId: string, amount: number) => {
    setEmployeeSalesInput(prev => ({
      ...prev,
      [staffId]: amount >= 0 ? amount : 0
    }));
  };

  // Drag & drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const uploadFile = async (file: File) => {
    if (!tenantId) return;
    setIsLoading(true);
    setMessage(null);
    try {
      const count = await importGrossReceiptsCSV(tenantId, file, file.name);
      setImportedFilename(file.name);
      setMessage({ 
        type: 'success', 
        text: `Successfully imported ${count} Gross Receipts records from ${file.name}.` 
      });
      // Re-trigger calculation
      await runCalculation();
    } catch (err: any) {
      console.error('Import error:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to parse Gross Receipts CSV. Ensure format is correct.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Finalize & Save Allocations
  const handleSaveAllocations = async () => {
    if (!tenantId || !calcResult || calcResult.allocations.length === 0) return;
    setIsSaving(true);
    setMessage(null);
    try {
      await saveShortfallAllocations(tenantId, calcResult.allocations);
      setMessage({ 
        type: 'success', 
        text: `Successfully saved shortfall allocations for ${calcResult.allocations.length} employees.` 
      });
      loadHistory();
    } catch (err: any) {
      console.error('Error saving:', err);
      setMessage({ type: 'error', text: 'Failed to save shortfall allocations.' });
    } finally {
      setIsSaving(false);
    }
  };

  // CSV Template generation
  const downloadTemplate = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Date,Gross Receipts,Charge Receipts,Charge Tips\n"
      + `${startDate || '2026-06-01'},15450.00,12300.00,1845.00\n`
      + `${endDate || '2026-06-02'},14200.00,11100.00,1665.00`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "gross_receipts_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // CSV Export of computed allocations
  const exportAllocations = () => {
    if (!calcResult || calcResult.allocations.length === 0) return;
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Employee Name,Employee Email,Start Date,End Date,Method,Share Percentage,Allocated Shortfall\n";
    
    calcResult.allocations.forEach(alloc => {
      const shareStr = (alloc.sharePercentage * 100).toFixed(2) + "%";
      csvContent += `"${alloc.staffName}","${alloc.email}","${alloc.startDate}","${alloc.endDate}","${alloc.allocationMethod}","${shareStr}",${alloc.allocatedShortfall.toFixed(2)}\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `shortfall_allocation_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sortedHistory = useMemo(() => {
    return [...historyList].sort((a, b) => {
      const dateA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime()) : 0;
      const dateB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime()) : 0;
      return dateB - dateA;
    });
  }, [historyList]);

  return (
    <div className="card p-6 md:p-8 space-y-8" id="gross-receipts-dashboard-card">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <Calculator className="text-blue-600" size={24} />
            Gross Receipts & IRS Form 8027 Shortfall Allocation
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Import store-level receipts, calculate IRS 8% tip safe-harbor thresholds, and allocate shortfalls.
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          className={`pb-2.5 px-5 font-bold text-sm transition-all flex items-center gap-2 ${
            activeTab === 'calculator' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
          onClick={() => setActiveTab('calculator')}
          id="tab-calculator-btn"
        >
          <Calculator size={16} /> Shortfall Calculator
        </button>
        <button
          className={`pb-2.5 px-5 font-bold text-sm transition-all flex items-center gap-2 ${
            activeTab === 'history' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
          onClick={() => setActiveTab('history')}
          id="tab-history-btn"
        >
          <Clock size={16} /> Allocation History
        </button>
      </div>

      {/* Notifications */}
      {message && (
        <div className={`p-4 rounded-xl flex items-start gap-3 border ${
          message.type === 'success' 
            ? 'bg-emerald-50 border-emerald-100 text-emerald-800' 
            : 'bg-red-50 border-red-100 text-red-800'
        }`} id="notification-banner">
          {message.type === 'success' ? <CheckCircle size={18} className="mt-0.5" /> : <AlertTriangle size={18} className="mt-0.5" />}
          <div className="text-sm font-medium">{message.text}</div>
        </div>
      )}

      {activeTab === 'calculator' ? (
        <>
          {/* Controls Grid */}
          <div className="flex flex-wrap items-center gap-3 bg-gray-50/50 p-4 rounded-2xl border border-gray-100">
            <div className="flex flex-col">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Pay Period / Range</label>
              <select 
                value={selectedPeriodId} 
                onChange={e => handlePeriodChange(e.target.value)}
                className="input-field text-sm font-medium pr-8"
                id="period-select"
              >
                {payrollPeriods.map(p => (
                  <option key={p.id} value={p.id}>
                    Payroll: {p.startDate} to {p.endDate} ({p.status})
                  </option>
                ))}
                <option value="monthly">Monthly (Current Month)</option>
                <option value="quarterly">Quarterly (Current Quarter)</option>
                <option value="annual">Annual (Current Year)</option>
                <option value="custom">Custom Date Range...</option>
              </select>
            </div>

            {['custom', 'monthly', 'quarterly', 'annual'].includes(selectedPeriodId) && (
              <>
                <div className="flex flex-col">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Start Date</label>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={e => {
                      setStartDate(e.target.value);
                      setSelectedPeriodId('custom');
                    }} 
                    className="input-field text-sm"
                    id="custom-start-date"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">End Date</label>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={e => {
                      setEndDate(e.target.value);
                      setSelectedPeriodId('custom');
                    }} 
                    className="input-field text-sm"
                    id="custom-end-date"
                  />
                </div>
              </>
            )}

            <div className="flex flex-col w-24">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">IRS Rate</label>
              <div className="relative">
                <input 
                  type="number" 
                  step="0.01" 
                  min="0.05" 
                  max="0.08" 
                  value={rate} 
                  onChange={e => setRate(Number(e.target.value))}
                  className="input-field text-sm pr-6"
                  id="allocation-rate"
                />
                <span className="absolute right-2.5 top-2 text-xs text-gray-400 font-bold">%</span>
              </div>
            </div>
          </div>

          {/* Main Grid: Upload & Key Figures */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Upload Box */}
            <div className="lg:col-span-1 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 tracking-tight uppercase">1. Import Sales CSV</h3>
                <button 
                  onClick={downloadTemplate}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
                  id="download-template-btn"
                >
                  <Download size={14} /> Template
                </button>
              </div>
              
              <div 
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`h-48 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center p-6 text-center cursor-pointer transition-all ${
                  isDragging 
                    ? 'border-blue-500 bg-blue-50/50 scale-[0.98]' 
                    : 'border-gray-200 hover:border-blue-400 hover:bg-gray-50/50'
                }`}
                id="drag-drop-zone"
              >
                <input 
                  type="file" 
                  accept=".csv" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                  className="hidden" 
                  id="gross-receipts-file-input"
                />
                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl mb-3">
                  <Upload size={24} />
                </div>
                <p className="text-sm font-bold text-gray-700">Drag & drop Sales CSV</p>
                <p className="text-xs text-gray-400 mt-1">or click to browse local files</p>
                {importedFilename && (
                  <p className="text-xs text-emerald-600 font-semibold mt-2 truncate max-w-full">
                    Active: {importedFilename}
                  </p>
                )}
              </div>
            </div>

            {/* Calculation Cards */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-sm font-bold text-gray-900 tracking-tight uppercase">2. Periodic Sales & Tip Metrics</h3>
              
              {isLoading ? (
                <div className="h-48 flex items-center justify-center bg-gray-50 rounded-2xl border border-gray-100">
                  <div className="text-sm text-gray-500 font-medium">Calculating shortfall variables...</div>
                </div>
              ) : calcResult ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  
                  <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Gross Receipts</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(calcResult.totalGrossReceipts)}</span>
                  </div>

                  <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Card Sales</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(calcResult.totalChargeReceipts)}</span>
                  </div>

                  <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">IRS Safe Harbor ({(rate * 100).toFixed(1)}%)</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(calcResult.targetTips)}</span>
                  </div>

                  <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Total Reported Tips</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(calcResult.totalReportedTips)}</span>
                  </div>

                  {/* Threshold Status */}
                  <div className={`col-span-2 md:col-span-4 p-5 rounded-2xl border flex items-center justify-between ${
                    calcResult.shortfall > 0 
                      ? 'bg-red-50/50 border-red-100 text-red-900' 
                      : 'bg-emerald-50/50 border-emerald-100 text-emerald-900'
                  }`} id="shortfall-status-box">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-xl mt-0.5 ${
                        calcResult.shortfall > 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'
                      }`}>
                        {calcResult.shortfall > 0 ? <AlertTriangle size={20} /> : <CheckCircle size={20} />}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold uppercase tracking-wider">
                          {calcResult.shortfall > 0 ? 'IRS Tip Shortfall Identified' : 'IRS 8% Safe Harbor Satisfied'}
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
                          {calcResult.shortfall > 0 
                            ? `Reported tips are under ${(rate * 100).toFixed(0)}% of gross receipts. You are legally required to allocate the shortfall of ${formatCurrency(calcResult.shortfall)} amongst eligible staff on Form 8027.` 
                            : "Total reported tips meet or exceed the IRS threshold. No tip shortfall allocation is required for this period."}
                        </p>
                      </div>
                    </div>
                    {calcResult.shortfall > 0 && (
                      <div className="text-right">
                        <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider block">Shortfall to Allocate</span>
                        <span className="text-2xl font-black text-red-600">{formatCurrency(calcResult.shortfall)}</span>
                      </div>
                    )}
                  </div>

                </div>
              ) : (
                <div className="h-48 flex items-center justify-center bg-gray-50 rounded-2xl border border-gray-100">
                  <div className="text-sm text-gray-400 font-medium">Select a period and import Gross Receipts CSV to see metrics.</div>
                </div>
              )}
            </div>

          </div>

          {/* Allocation Method Selection & Detailed Table */}
          {calcResult && calcResult.shortfall > 0 && (
            <div className="space-y-6 border-t border-gray-100 pt-8" id="allocation-method-section">
              
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-gray-900 tracking-tight flex items-center gap-2">
                    <Percent className="text-blue-600" size={18} />
                    3. Choose Allocation Method & View Breakdown
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    IRS Form 8027 specifies three approved methods. Select the method that fits your operations.
                  </p>
                </div>

                {/* Method Toggles */}
                <div className="flex bg-gray-100 p-1 rounded-xl w-fit">
                  <button
                    onClick={() => setAllocationMethod('hours_worked')}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                      allocationMethod === 'hours_worked' 
                        ? 'bg-white text-blue-600 shadow-sm' 
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                    id="method-hours-btn"
                  >
                    <Clock size={14} /> Hours Worked
                  </button>
                  <button
                    onClick={() => setAllocationMethod('gross_receipts')}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                      allocationMethod === 'gross_receipts' 
                        ? 'bg-white text-blue-600 shadow-sm' 
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                    id="method-gross-btn"
                  >
                    <FileSpreadsheet size={14} /> Gross Receipts
                  </button>
                  <button
                    onClick={() => setAllocationMethod('good_faith')}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                      allocationMethod === 'good_faith' 
                        ? 'bg-white text-blue-600 shadow-sm' 
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                    id="method-good-faith-btn"
                  >
                    <User size={14} /> Good Faith Split
                  </button>
                </div>
              </div>

              {/* Allocation Info Callout */}
              <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50 flex gap-3 text-blue-900">
                <Info size={16} className="text-blue-600 mt-0.5 shrink-0" />
                <div className="text-xs space-y-1">
                  {allocationMethod === 'hours_worked' && (
                    <p>
                      <strong>Hours Worked Method:</strong> Shortfall is allocated proportionally to employees based on hours logged in their Labor Records. Ensure labor files have been imported under the <em>Staff Recon</em> tab.
                    </p>
                  )}
                  {allocationMethod === 'gross_receipts' && (
                    <p>
                      <strong>Gross Receipts / Sales Method:</strong> Shortfall is allocated proportionally based on individual employee sales. <strong>You can enter each employee's sales directly in the table below</strong> to run calculations.
                    </p>
                  )}
                  {allocationMethod === 'good_faith' && (
                    <p>
                      <strong>Good Faith Agreement:</strong> Allocates the shortfall equally among all tipped employees who reported tips during this payroll period.
                    </p>
                  )}
                </div>
              </div>

              {/* Breakdown Table */}
              <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm" id="breakdown-table-container">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                      <th className="py-4 px-6">Employee</th>
                      {allocationMethod === 'hours_worked' && <th className="py-4 px-6 text-center">Hours Worked</th>}
                      {allocationMethod === 'gross_receipts' && <th className="py-4 px-6 text-center w-48">Employee Sales ($)</th>}
                      <th className="py-4 px-6 text-center">Share %</th>
                      <th className="py-4 px-6 text-right">Allocated Shortfall</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-sm">
                    {calcResult.allocations.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 px-6 text-center text-gray-400 font-medium">
                          {allocationMethod === 'hours_worked' 
                            ? 'No active labor hours found for this period. Import labor records first.' 
                            : 'No employees found to allocate. Ensure employees have transactions in this period.'}
                        </td>
                      </tr>
                    ) : (
                      calcResult.allocations.map((alloc) => (
                        <tr key={alloc.staffId} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-4 px-6 font-semibold text-gray-900">
                            {alloc.staffName}
                            <span className="block text-xs text-gray-400 font-normal mt-0.5">{alloc.email}</span>
                          </td>
                          
                          {allocationMethod === 'hours_worked' && (
                            <td className="py-4 px-6 text-center font-bold text-gray-700">
                              {formatHours(alloc.hoursWorked || 0)}
                            </td>
                          )}

                          {allocationMethod === 'gross_receipts' && (
                            <td className="py-4 px-6 text-center">
                              <div className="relative max-w-[150px] mx-auto">
                                <span className="absolute left-3.5 top-2.5 text-gray-400 font-semibold text-xs">$</span>
                                <input 
                                  type="number" 
                                  value={employeeSalesInput[alloc.staffId] || 0}
                                  onChange={e => handleEmployeeSalesChange(alloc.staffId, Number(e.target.value))}
                                  className="input-field text-xs pl-7 text-center font-bold"
                                  placeholder="0.00"
                                />
                              </div>
                            </td>
                          )}

                          <td className="py-4 px-6 text-center">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-600">
                              {formatPercentage(alloc.sharePercentage * 100)}
                            </span>
                          </td>

                          <td className="py-4 px-6 text-right font-bold text-red-600">
                            {formatCurrency(alloc.allocatedShortfall)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={exportAllocations}
                  className="btn btn-secondary text-sm flex items-center gap-2 font-bold px-5 py-2.5"
                  id="export-shortfall-csv-btn"
                >
                  <Download size={16} /> Export Mapped CSV
                </button>
                <button
                  onClick={handleSaveAllocations}
                  disabled={isSaving || calcResult.allocations.length === 0}
                  className="btn btn-primary text-sm flex items-center gap-2 font-bold px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-55"
                  id="finalize-shortfall-btn"
                >
                  <Save size={16} /> 
                  {isSaving ? 'Saving...' : 'Finalize & Store Allocation'}
                </button>
              </div>

            </div>
          )}
        </>
      ) : (
        /* History Tab Content */
        <div className="card overflow-hidden" id="shortfall-history-container">
          <div className="p-6 md:p-8 border-b border-gray-150 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Shortfall Allocation History</h3>
              <p className="text-xs text-gray-500 mt-1">Transaction history of finalized IRS 8% safe harbor true-ups</p>
            </div>
            <button
              onClick={loadHistory}
              className="btn btn-secondary text-xs font-bold py-2 px-4"
              disabled={isHistoryLoading}
            >
              Refresh History
            </button>
          </div>
          
          {isHistoryLoading ? (
            <div className="p-20 text-center text-gray-500">Loading history...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Date</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Master TX ID</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Item ID</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Staff Name</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sortedHistory.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-8 py-20 text-center text-gray-400 font-medium">
                        No shortfall allocation history found.
                      </td>
                    </tr>
                  ) : (
                    sortedHistory.map(item => (
                      <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-8 py-4 text-gray-600">{item.startDate} to {item.endDate}</td>
                        <td className="px-8 py-4 font-mono text-[10px] text-gray-400 relative">
                          <span title={item.masterTxId}>{item.masterTxId ? item.masterTxId.slice(-6) : '-'}</span>
                        </td>
                        <td className="px-8 py-4 font-mono text-[10px] text-gray-400">{item.id}</td>
                        <td className="px-8 py-4 font-semibold text-gray-900">
                          {item.staffName}
                          <span className="block text-xs text-gray-400 font-normal mt-0.5">{item.email}</span>
                        </td>
                        <td className="px-8 py-4 text-right font-bold text-red-600">{formatCurrency(item.allocatedShortfall)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};
